import { getAccessActionPolicy } from '$lib/permissions/accessUiCatalog.js';

/**
 * Check if a user has permission for a specific resource and access type
 * @param {Object} user - The user object
 * @param {string} studyId - The study ID (null for global permissions)
 * @param {string} resource - The resource type (route, menu, field, action)
 * @param {string} identifier - The specific resource identifier
 * @param {string} access - The access type (read, write, create, delete)
 * @returns {Promise<boolean>} - Whether the user has permission
 */
export async function checkPermission(resource, access, identifier = null) {
  try {
    const response = await fetch('/api/v1/permissions/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        resource,
        access,
        identifier
      })
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.hasPermission;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

/**
 * Get all permissions for a user in a specific study
 * @param {Object} user - The user object
 * @param {string} studyId - The study ID
 * @returns {Promise<Array>} - Array of permissions
 */
export async function getUserStudyPermissions(user, studyId) {
  try {
    const response = await fetch(`/api/v1/permissions/study/${studyId}`);
    
    if (!response.ok) {
      throw new Error('Failed to get study permissions');
    }

    const result = await response.json();
    return result.permissions;
  } catch (error) {
    console.error('Error getting user study permissions:', error);
    return [];
  }
}

/**
 * Get all roles a user has in a specific study
 * @param {Object} user - The user object
 * @param {string} studyId - The study ID
 * @returns {Promise<Array>} - Array of roles
 */
export async function getUserStudyRoles(user, studyId) {
  try {
    const response = await fetch(`/api/v1/roles/study/${studyId}/user`);
    
    if (!response.ok) {
      throw new Error('Failed to get study roles');
    }

    const result = await response.json();
    return result.roles;
  } catch (error) {
    console.error('Error getting user study roles:', error);
    return [];
  }
}

/**
 * Check if a user has any of the specified roles in a study
 * @param {Object} user - The user object
 * @param {string} studyId - The study ID
 * @param {Array<string>} roleNames - Array of role names to check
 * @returns {Promise<boolean>} - Whether the user has any of the roles
 */
export async function hasAnyStudyRole(user, studyId, roleNames) {
  try {
    const response = await fetch(`/api/v1/roles/study/${studyId}/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ roleNames })
    });

    if (!response.ok) {
      throw new Error('Failed to check study roles');
    }

    const result = await response.json();
    return result.hasRole;
  } catch (error) {
    console.error('Error checking study roles:', error);
    return false;
  }
} 

/**
 * Study-configurable UI action (see accessUiCatalog.js).
 * Uses studyProperties.permissions.actionRoles[actionKey] when set; otherwise catalog defaultRoles.
 * When the policy has requiresWrite, user.readOnly must be false (pass readOnly from session).
 *
 * @param {string} actionKey - Catalog key, e.g. studies.add, sidebar.menu.eventReport
 * @param {string[]} userRoles
 * @param {object|null|undefined} studyProperties
 * @param {boolean} readOnly - user.readOnly
 * @returns {boolean}
 */
export function hasAccessUiAction(actionKey, userRoles, studyProperties, readOnly) {
  const policy = getAccessActionPolicy(actionKey);
  if (!policy) return false;
  if (policy.requiresWrite && readOnly) return false;

  const stored = studyProperties?.permissions?.actionRoles?.[actionKey];
  const allowed =
    Array.isArray(stored) && stored.length > 0 ? stored : policy.defaultRoles;
  if (!allowed.length) return false;

  const allowedLower = new Set(allowed.map((r) => String(r).toLowerCase()));
  return (userRoles || []).some((r) => allowedLower.has(String(r).toLowerCase()));
}

/**
 * Audit log modal / button (catalog auditLogs.view). Optional study override via actionRoles.
 * @param {string[]} userRoles
 * @param {object|null|undefined} [studyProperties]
 * @param {boolean} [readOnly] - when true and policy requiresWrite, denies (catalog uses requiresWrite: false)
 */
export function hasAuditLogAccess(userRoles, studyProperties = null, readOnly = false) {
  return hasAccessUiAction('auditLogs.view', userRoles, studyProperties, readOnly);
}

/**
 * Diary / subject form actions on /studyForms (add or edit). Delegates to hasAccessUiAction.
 *
 * @param {string} actionKey - e.g. studyForms.add, studyForms.edit
 * @param {string[]} userRoles
 * @param {object|null} studyProperties
 * @param {boolean} readOnly
 */
export function hasStudyFormAction(actionKey, userRoles, studyProperties, readOnly) {
  return hasAccessUiAction(actionKey, userRoles, studyProperties, readOnly);
}
